import { isSameDay as checkIsSameDay, parseISO } from "date-fns";
import { memo } from "react";

import type { DateFormatting } from "@/components/FormattedDate/FormattedDate";
import { formatDate, stripRelativeFromDateFormat } from "@/components/FormattedDate/FormattedDate";
import type { i18n } from "@/translations";
import { useTranslation } from "@/translations";

/**
 * How a start/end pair is rendered:
 * - `withTime` (default): long date + time on the start; the end shows time only when it falls on
 *   the same day, otherwise the full date + time.
 * - `noTime`: date only, no times.
 * - `shortWithTime`: compact date + time on the start; the end shows time only on the same day.
 */
export type DateRangeFormat = "withTime" | "noTime" | "shortWithTime";

/**
 * Props for {@link DateRange}.
 */
export interface DateRangeProps {
  /** Range start. ISO string, epoch ms, or `Date`. */
  start: string | number | Date;
  /** Range end. ISO string, epoch ms, or `Date`. */
  end: string | number | Date;
  /** Render inline when the range is part of a sentence. */
  inline?: boolean;
  /** Use the emphasized body treatment. */
  emphasized?: boolean;
  /** Formatting variant. Defaults to `"withTime"`. See {@link DateRangeFormat}. */
  format?: DateRangeFormat;
}

// Single source of truth for how a start/end pair is rendered. `DateRange` uses it to draw two
// `<time>` elements; callers that need a plain string (translation interpolation, string-only props
// like a radio label) use `formatDateRange`.
function resolveDateRangeParts(
  i18nInstance: i18n,
  start: string | number | Date,
  end: string | number | Date,
  format: DateRangeFormat,
): { start: string; end: string } {
  const isSameDay = checkIsSameDay(
    typeof start === "string" ? parseISO(start) : start,
    typeof end === "string" ? parseISO(end) : end,
  );

  let startFormat: DateFormatting = "datetime";
  if (format === "noTime") {
    startFormat = "date";
  } else if (format === "shortWithTime") {
    startFormat = "datetimeShort";
  }
  const endFormat: DateFormatting =
    isSameDay && format !== "noTime" ? "time" : stripRelativeFromDateFormat(startFormat);

  let startDate = formatDate(i18nInstance, startFormat, start);
  const endDate = formatDate(i18nInstance, endFormat, end);

  // Drop a redundant current-year suffix from the start when the end already carries it.
  const currentYear = new Date().getFullYear().toString();
  if (startDate.endsWith(currentYear) && endDate.endsWith(currentYear)) {
    startDate = startDate.replace(" " + currentYear, "");
  }

  return { start: startDate, end: endDate };
}

/**
 * Formats a start/end pair as a single `"start - end"` string, using the same rules as
 * {@link DateRange}. Use this where a string is required (translation interpolation, string-only
 * props such as a radio label); prefer the {@link DateRange} component when rendering directly.
 */
export function formatDateRange(
  i18nInstance: i18n,
  {
    start,
    end,
    format = "withTime",
  }: { start: string | number | Date; end: string | number | Date; format?: DateRangeFormat },
): string {
  const parts = resolveDateRangeParts(i18nInstance, start, end, format);

  return `${parts.start} - ${parts.end}`;
}

/**
 * Renders a start/end pair as two `<time>` elements joined by a dash, formatted per `format`.
 * For a plain string of the same range, use {@link formatDateRange}.
 */
export const DateRange = memo(function DateRange({
  start,
  end,
  inline = false,
  emphasized = false,
  format = "withTime",
}: DateRangeProps): React.ReactNode {
  const { i18n } = useTranslation();
  const parts = resolveDateRangeParts(i18n, start, end, format);
  const Component = inline ? "span" : "p";

  return (
    <Component className={emphasized ? "text-body-emphasis" : undefined}>
      <time>{parts.start}</time>
      {" - "}
      <time>{parts.end}</time>
    </Component>
  );
});
